import type {LDContext, LDReactClient} from '@launchdarkly/react-sdk'
import {createClient} from '@launchdarkly/react-sdk'
import type {EnvironmentT} from '@freckle/educator-entities/ts/config/environment'
import {getEnvironment} from '@freckle/educator-entities/ts/config/environment'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'

// Client-side IDs are public: they only identify a LaunchDarkly environment. Without one for the
// current environment the client isn't started, and every flag evaluates to its default.
const CLIENT_SIDE_IDS: Record<EnvironmentT, string> = {
  production: '690bd1cd9d09eb09b19f8a8e',
  staging: '6ad7d330bc840f0722e5d941',
  test: '690bd1cd9d09eb09b19f8a8d',
  demo: '6ad7d34a4beeda07c162e24c',
  localhost: '6ad7d3802c6a690796c92202'
}

export function launchDarklyClientSideId(): string | null {
  const clientSideId = CLIENT_SIDE_IDS[getEnvironment(window.location.hostname) ?? 'localhost']
  return clientSideId !== '' ? clientSideId : null
}

// How long to wait for flags before settling on default values, so a slow or blocked LaunchDarkly
// never holds up a page waiting on a variation
export const INITIALIZATION_TIMEOUT_SECONDS = 3

// Stands in until the teacher has loaded, since the SDK can't change the context of a client before
// it starts. It's never started, so flag hooks return their defaults. Without `fetchGoals: false`
// the browser SDK fetches Experimentation goals as soon as a client is created, started or not.
export function placeholderClient(): LDReactClient {
  return createClient(
    'placeholder',
    {kind: 'user', key: 'placeholder', anonymous: true},
    {sendEvents: false, fetchGoals: false}
  )
}

export function teacherContext(teacher: TeacherAttrs): LDContext {
  return {
    kind: 'user',
    key: `teacher-${teacher.id}`,
    countryCode: teacher.countryCode ?? null,
    language: teacher.language,
    role: teacher.role ?? null,
    environment: teacher.environment ?? null,
    schoolId: teacher.schoolId ?? null,
    districtId: teacher.districtId ?? null,
    identityProvider: teacher.identityProvider ?? null,
    idpManaged: teacher.idpManaged
  }
}
